import { Image, ImageProps, Box } from "@chakra-ui/react";
import {
  isAllowedExternalImageUrl,
  isHeliusImageUrl,
} from "@syla/shared/helpers/isAllowedExternalImageUrl";
import React, { useEffect, useState } from "react";
import missingImageIcon from "../../images/thumbnails/missingImageIcon.svg";
import noPaddingMissingImageIcon from "../../images/thumbnails/noPaddingMissingImageIcon.svg";
import { SkeletonCircle } from "./Skeletons";

type ImageWithMissingSrcProps = ImageProps & {
  isLoading?: boolean;
  noPaddingIcon?: boolean;
};

export function ImageWithMissingSrc({
  src,
  ...props
}: ImageWithMissingSrcProps): React.ReactElement {
  if (src && isHeliusImageUrl(src)) {
    return <ExternalImageWithSizeCheck src={src} {...props} />;
  }

  return <BasicImageWithMissingSrc src={src} {...props} />;
}

function BasicImageWithMissingSrc({
  src,
  isLoading,
  noPaddingIcon = false,
  ...props
}: ImageWithMissingSrcProps): React.ReactElement {
  const fallbackIcon = getFallbackIcon(noPaddingIcon);

  if (isLoading) {
    return (
      <Box {...props}>
        <SkeletonCircle />
      </Box>
    );
  }

  return (
    <Image
      {...props}
      src={src || fallbackIcon}
      onError={getFallbackImageOnErrorHandler(fallbackIcon)}
    />
  );
}

/**
 * Avoid unnecessary HEAD network requests for external images by caching the
 * URL check result by src.
 *
 * The value is a Promise so that multiple components rendering the same image
 * at the same time share the same HEAD request.
 */
const ExternalImageUrlAllowedCache = new Map<string, Promise<boolean>>();

/**
 * Some external image URLs may point to very large images. This checks whether
 * the image URL is allowed before rendering it. If the image is blocked, we
 * show the fallback icon instead.
 *
 * The better solution would be to resize, compress and cache the image, but we
 * don't have a framework for that yet.
 */
function ExternalImageWithSizeCheck({
  src,
  isLoading,
  noPaddingIcon = false,
  ...props
}: ImageWithMissingSrcProps & {
  src: string;
}): React.ReactElement {
  const fallbackIcon = getFallbackIcon(noPaddingIcon);

  const [isCheckingImageUrl, setIsCheckingImageUrl] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    let isCancelled = false; // prevent state updates after unmount or src change

    async function checkImageUrl(src: string) {
      setIsCheckingImageUrl(true);
      setIsBlocked(false);

      try {
        const isAllowed = await getCachedIsAllowedExternalImageUrl(src);

        if (!isCancelled) {
          setIsBlocked(!isAllowed);
        }
      } catch {
        if (!isCancelled) {
          // If the URL check fails, block the image.
          setIsBlocked(true);
        }
      } finally {
        if (!isCancelled) {
          setIsCheckingImageUrl(false);
        }
      }
    }

    checkImageUrl(src);

    return () => {
      isCancelled = true;
    };
  }, [src]);

  if (isLoading || isCheckingImageUrl) {
    return (
      <Box {...props}>
        <SkeletonCircle />
      </Box>
    );
  }

  return (
    <Image
      {...props}
      src={isBlocked ? fallbackIcon : src}
      onError={getFallbackImageOnErrorHandler(fallbackIcon)}
    />
  );
}

function getCachedIsAllowedExternalImageUrl(src: string): Promise<boolean> {
  const cached = ExternalImageUrlAllowedCache.get(src);

  if (cached) {
    return cached;
  }

  const promise = isAllowedExternalImageUrl(src).catch((error: unknown) => {
    // Do not permanently cache transient network/CORS failures.
    ExternalImageUrlAllowedCache.delete(src);
    throw error;
  });

  ExternalImageUrlAllowedCache.set(src, promise);

  return promise;
}

function getFallbackIcon(noPaddingIcon: boolean): string {
  return noPaddingIcon ? noPaddingMissingImageIcon : missingImageIcon;
}

function getFallbackImageOnErrorHandler(
  fallbackIcon: string,
): NonNullable<ImageProps["onError"]> {
  return (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const image = event.currentTarget;

    if (image.getAttribute("src") !== fallbackIcon) {
      image.src = fallbackIcon;
    }
  };
}
